import * as WebFont from 'webfontloader';
import debug from './debug';
import { htmlStringToNode, nodeListToArray } from './dom';
/**
 * Load font based on font family name
 * This guarantees the actual font is loaded, not only its css
 * DOC: https://github.com/typekit/webfontloader
 *
 * @return Promise
 */
export const loadAllWebFonts = (webFontConfig) => {
    if (!webFontConfig) {
        return Promise.resolve('');
    }
    const extraConfig = (resolve) => ({
        active: () => resolve(''),
        inactive: () => {
            debug('Failed to load some or all fonts.');
            // We don't want failed fonts to
            // break an ad, so we always resolve.
            resolve('');
        },
        fontinactive: (familyName) => {
            debug(`Cannot load font: ${familyName}.`);
        },
        fontactive: (familyName) => {
            debug(`Loaded font: ${familyName}.`);
        },
    });
    return new Promise(resolve => {
        var _a, _b;
        try {
            WebFont.load(Object.assign(Object.assign({}, webFontConfig), extraConfig(resolve)));
        }
        catch (e) {
            debug('Cannot load font', webFontConfig, e);
            // Found case when font fails that above functions cannot catch:
            // Case: Google's "text" includes invalid char or emojis - Fallback to load the whole font without text string
            WebFont.load(Object.assign(Object.assign(Object.assign({}, webFontConfig), extraConfig(resolve)), { google: {
                    families: (_b = (_a = webFontConfig.google) === null || _a === void 0 ? void 0 : _a.families, (_b !== null && _b !== void 0 ? _b : [])),
                } }));
        }
    });
};
/**
 * Convert font styles back to font variants
 * Ex:
 * - 700, 'italic' => '700italic'
 * - 'normal' => 'regular',
 * - 'italic' => 'italic',
 * - 100 => '100',
 */
export const convertStylesToVariant = (fontWeight = '', fontStyle = '') => {
    const weight = fontWeight === 'normal' ? 'regular' : fontWeight || '';
    const style = fontStyle === 'normal' ? '' : fontStyle || '';
    return `${weight}${style}`;
};
/**
 * Translate font.family & fallback to standard CSS font family declaration
 * Ex:
 * - Slabo 27px, serif => "Slabo 27px", serif
 * - Helvetica, Arial, handwriting=> "Helvetica", "Arial", cursive
 */
export const translateFontCSS = (family, category = '') => {
    // ensure font is in double quote
    const split = family.split(',');
    const families = split.map(fam => `"${fam.trim()}"`).join(', ');
    const fallback = category ? `, ${toValidFontFallback(category)}` : '';
    return families + fallback;
};
/**
 * convert font to a valid font fallback
 * - 'display' => 'cursive'
 * - 'handwriting' => 'cursive'
 * - 'My Font' => 'My Font'
 */
export const toValidFontFallback = (fontName) => {
    const fallbackMap = {
        display: 'cursive',
        handwriting: 'cursive',
    };
    return fallbackMap[fontName] || fontName;
};
/**
 * Find inline basic font variants from text: 700, italic, 700italic
 */
const tagMap = {
    b: '700',
    i: 'italic',
};
export const findInlineVariants = (htmlString) => {
    const node = htmlStringToNode(htmlString);
    const childrenNodes = nodeListToArray(node.querySelectorAll('*'));
    // extract any Bold, Italic tag
    const tags = childrenNodes
        .map(n => n.tagName.toLowerCase())
        .filter((tag) => ['b', 'i'].indexOf(tag) > -1);
    const result = tags
        // unique
        .filter((tag, idx, c) => c.indexOf(tag) === idx)
        .map(tag => tagMap[tag]);
    // does it has combination of bold and italic
    const hasBoldItalic = result.indexOf('700') > -1 && result.indexOf('italic') > -1;
    return result.concat(hasBoldItalic ? ['700italic'] : []);
};
